/*
 * Copyright (c) 2025. World Wide Generation Ltd
 */

import { Badge, DropdownItem, DropdownMenu, DropdownToggle, UncontrolledDropdown } from 'reactstrap';
import { BatchJob, ListJobsResponse } from '../../../api/ai-supervisor';
import { DATE, formatDateNonUTC } from '../../../utils/date';
import { useMemo } from 'react';
import { SurveySettingsHeader } from '../../survey-configuration/partials/SurveySettingsHeader';

interface BreadcrumbBarProps {
  jobsData?: ListJobsResponse;
  selectedBatchId?: string;
  onBatchChange?: (batchId: string) => void;
}

export const BreadcrumbBar = ({ jobsData, selectedBatchId, onBatchChange }: BreadcrumbBarProps) => {
  const selectedBatch = useMemo(() => {
    return jobsData?.jobs?.find((job) => job.batchId === selectedBatchId);
  }, [jobsData?.jobs, selectedBatchId]);

  return (
    <SurveySettingsHeader>
    <div className='ms-auto'>
      {jobsData?.jobs && jobsData.jobs.length > 1 && onBatchChange && (
        <UncontrolledDropdown>
          <DropdownToggle caret>
            {getBatchToggleLabel(selectedBatch)}
          </DropdownToggle>
          <DropdownMenu end>
            {jobsData.jobs.map((batch) => (
              <DropdownItem
                key={batch.batchId}
                onClick={() => onBatchChange(batch.batchId)}
                active={batch.batchId === selectedBatchId}
              >
                {formatBatchLabel(batch)}
              </DropdownItem>
            ))}
          </DropdownMenu>
        </UncontrolledDropdown>
      )}
    </div>
    </SurveySettingsHeader>
  );
}

// Get simple text label for dropdown toggle
const getBatchToggleLabel = (batch: BatchJob | undefined) => {
  if (!batch) return 'Select a batch';
  return `Auto-Answer (Model: ${batch.config.settings.model ?? 'unknown'}) ${formatDateNonUTC(batch.createdAt, DATE.AT_TIME)}`;
};

// Format batch label for dropdown display
const formatBatchLabel = (batch: BatchJob) => {
  const createdDate = formatDateNonUTC(batch.createdAt, DATE.AT_TIME);
  const statusColor =
    batch.status === 'processing' || batch.status === 'pausing'
      ? 'primary'
      : batch.status === 'completed'
      ? 'success'
      : batch.status === 'failed'
      ? 'danger'
      : batch.status === 'paused'
      ? 'warning'
      : 'secondary';
  const statusLabel =
    batch.status === 'processing'
      ? 'Processing'
      : batch.status === 'pausing'
      ? 'Pausing'
      : batch.status === 'paused'
      ? 'Paused'
      : batch.status === 'completed'
      ? 'Completed'
      : batch.status === 'failed'
      ? 'Failed'
      : batch.status === 'cancelled'
      ? 'Cancelled'
      : batch.status;
  return (
    <div className='d-flex align-items-center justify-content-between w-100'>
      <div className='d-flex flex-column nowrap'>
        Auto-Answer (Model: {batch.config.settings.model ?? 'unknown'}) <span className='fw-bold'>{createdDate}</span>
        <span className='small'>{batch.batchId}</span>
      </div>
      <Badge color={statusColor} className='ms-2'>
        {statusLabel}
      </Badge>
    </div>
  );
};
